import { Box, Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { DELIVERY_DATE_REFERENCE_LABELS } from "../constants/deliveryPackaging"
import { useGetDeliveryPackaging } from "../hooks/useGetDeliveryPackaging"

import { CompanyPreferenceSection } from "./CompanyPreferenceSection"
import { EditDeliveryPackagingDialog } from "./delivery-packaging/EditDeliveryPackagingDialog"
import { PreferenceTwoColumnRow } from "./PreferenceTwoColumnRow"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface DeliveryAndPackagingProps {
  locationId: string
}

export const DeliveryAndPackaging: FC<DeliveryAndPackagingProps> = ({ locationId }) => {
  const { data, isFetching, refetch } = useGetDeliveryPackaging(locationId)
  const { hasPermissions } = usePermissionManager()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const handleOpenDialog = () => {
    setIsDialogOpen(true)
  }

  const handleCloseDialog = (shouldRefetch?: boolean) => {
    setIsDialogOpen(false)
    if (shouldRefetch) {
      refetch()
    }
  }

  if (!data) {
    return (
      <>
        <Fade in unmountOnExit timeout={500}>
          <InformationMissingAlert
            title={canEdit ? "Start by entering data" : "No configuration details have been added"}
            action={
              canEdit && (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={handleOpenDialog}
                  data-testid='delivery-packaging-add-button'
                >
                  Add Delivery & Packaging Data
                </Button>
              )
            }
          />
        </Fade>
        {isDialogOpen && (
          <EditDeliveryPackagingDialog onClose={handleCloseDialog} locationId={locationId} initialData={null} />
        )}
      </>
    )
  }

  const dateReferenceLabel = data.preferredDeliveryDateReference
    ? DELIVERY_DATE_REFERENCE_LABELS[data.preferredDeliveryDateReference]
    : null

  return (
    <>
      <CompanyPreferenceSection
        title='Delivery & Packaging Data'
        onEdit={handleOpenDialog}
        canEdit={canEdit}
        editButtonTestId='delivery-packaging-edit-button'
        isLoading={isFetching}
      >
        <Box className='flex flex-col'>
          <PreferenceTwoColumnRow
            leftContent='PO Date Reference'
            rightContent={dateReferenceLabel ?? "-"}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow
            leftContent='Insure Small Parcel'
            rightContent={data.insureSmallParcel ? "Yes" : "No"}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow leftContent='Packaging Notes' rightContent={data.packagingInstructions ?? "-"} />
        </Box>
      </CompanyPreferenceSection>
      {isDialogOpen && (
        <EditDeliveryPackagingDialog onClose={handleCloseDialog} locationId={locationId} initialData={data} />
      )}
    </>
  )
}
